/*
 * @Author: 张印 hbzyin@163.com
 * @Date: 2023-07-18 09:09:18
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2023-08-25 19:06:54
 * @FilePath: /code/mall-fe/src/comps/ShopProListPC.tsx
 * @Description: PC 端店铺列表组件
 *
 */
import { Spin } from 'antd';
import { searchSpuList, shopPageSkuList } from 'api/shop';
import useAreaCookie from 'hooks/useAreaCookie';
import jsurl from 'jsurl';
import { LocContext, UserContext } from 'libs/contexts';
import { FeatureEnum, isFeatureActive } from 'libs/features';
import { useRouter } from 'next/router';
import { clone } from 'rambdax';
import { useContext, useEffect, useRef, useState } from 'react';
import { Category } from './CategoryBreadcrumb';
import Container from './Container';
import ListFilter from './ListFilter';
import ListRuleBar from './ListRuleBar';
import ListSearchResult from './ListSearchResult';
import SearchListContent from './SearchListContent';
import { useNetActionPriceContext } from 'libs/contexts';
import AgreementSelector from '@/biz-components/web/AgreementSelector';
import ContractSelector from '@/biz-components/web/ContractSelector';
import { UserSource } from 'libs/enums';
export type ListData = {
  total: number;
  pageNo: number;
  pageSize: number;
  totalPage: number;
  datas: Array<SpuEntity>;
};

type ShopProListPCProps = {
  isSearchPage?: boolean;
  shopData: any;
};

const ShopProListPC = ({ isSearchPage, shopData }: ShopProListPCProps) => {
  // 获取定位信息
  const { location } = useContext(LocContext);
  const userInfo = useContext(UserContext);
  const getNetPriceList = useNetActionPriceContext();
  const { areaCode, getCookieAreacode } = useAreaCookie();
  const [listData, setListData] = useState<ListData>({
    total: null,
    pageNo: null,
    totalPage: null,
    pageSize: 30,
    datas: [],
  });
  // 商品分类数据
  const [shopCustomCategoryList, setShopCustomCategoryList] = useState<Array<Category>>([]);
  const router = useRouter();
  const searchParams = useRef<any>({
    sorts: [{ sortField: '', orderBy: 1 }],
    keywords: '',
  });
  const [filterList, setFilterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [areaLevelCode, setAreaLevelCode] = useState('');
  const [upstreamSupplierSysNoList, setUpstreamSupplierSysNoList] = useState([]);
  const navTabColor = shopData.navigationSetting?.jsonConfig?.navTabColor;
  const primaryColor = navTabColor ? `#${navTabColor}` : undefined;

  useEffect(() => {
    if (!areaCode) return;
    (async () => {
      checkParams();
      getList();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, areaCode, location]);

  // 参数校验
  const checkParams = () => {
    const { query } = router;
    if (query.params) {
      searchParams.current = {
        ...jsurl.parse(query.params),
      };
      if (searchParams.current.keywords) {
        searchParams.current.keywords = decodeURIComponent(searchParams.current.keywords);
      }
    }
  };

  // 结合商品过滤条件
  const combineFilter = (sku) => {
    const arr = [];
    if (!!sku.upstreamSupplierList?.length) {
      arr.push({
        type: 'upstreamSupplierSysNoList',
        name: '配送商',
        values: sku.upstreamSupplierList.map((v) => ({
          ...v,
          name: v.supplierName,
          val: [
            {
              type: 'upstreamSupplierSysNoList',
              sysNo: v.sysNo,
              name: v.supplierName,
            },
          ],
        })),
      });
    }
    if (sku.spuAttributeList?.length > 0) {
      sku.spuAttributeList.forEach((v) => {
        arr.push({
          type: 'spuAttributeList',
          ...v,
          values: v.values?.map((v2) => ({
            ...v2,
            type: 'spuAttributeList',
            val: [
              {
                type: 'spuAttributeList',
                sysNo: v2.sysNo,
                pSysNo: v.sysNo,
                name: v2.name,
              },
            ],
          })),
        });
      });
    }
    if (sku.brandList?.length > 0) {
      arr.push({
        type: 'brands',
        name: '品牌',
        values: sku.brandList.map((v) => ({
          ...v,
          val: [
            {
              type: 'brands',
              sysNo: v.sysNo,
            },
          ],
        })),
      });
    }

    const newArr = [];
    const list = clone(arr);
    const relList = [];
    list.forEach((v) => {
      const index = newArr.findIndex((c) => c.name === v.name);
      if (index > -1) {
        v.values.forEach((m) => {
          const index2 = newArr[index].values.findIndex((c) => c.name === m.name);
          if (index2 > -1) {
            newArr[index].values[index2].val.push(m.val[0]);
            relList.push({ ...m, relative: newArr[index].values[index2] });
          } else {
            newArr[index].values.push(m);
          }
        });
      } else {
        newArr.push(v);
      }
    });

    return newArr;
  };

  // 获取商品列表
  async function getList() {
    const { shopSysNo, pageSysNo, isCustomCategory } = router.query;
    const catIdArr = searchParams.current?.catId?.split(',') ?? null;
    const categorySysNoList = catIdArr ? [catIdArr[catIdArr.length - 1]] : [];
    const json = {
      areaLevelCode: getCookieAreacode(),
      pageNo: 1,
      pageSize: 30,
      shopSysNoList: [shopSysNo],
      ...searchParams.current,
      shopSkuListPageSysNo: pageSysNo,
    };
    if (isCustomCategory) {
      json.shopCustomCategorySysNoList = categorySysNoList;
      json.catId = undefined;
    } else {
      json.categorySysNoList = categorySysNoList;
    }
    setLoading(true);
    const res = isSearchPage ? await searchSpuList({ json }) : await shopPageSkuList({ json });
    setLoading(false);
    if (res.code === 200) {
      setListData(res.data.spuList);
      setFilterList(combineFilter(res.data));
      setShopCustomCategoryList(res.data.shopCustomCategoryList);
      getNetPriceList(res.data.spuList?.datas, (list) => {
        setListData({
          ...res.data.spuList,
          datas: list,
        });
      });
    }
  }

  function updateSearch(updateParams) {
    if (updateParams.areaLevelCode) {
      updateParams.agreementSysNoList = [];
      updateParams.contractAgreementSysNoList = [];
      setAreaLevelCode(updateParams.areaLevelCode);
    }
    if (updateParams.upstreamSupplierSysNoList) {
      updateParams.agreementSysNoList = [];
      updateParams.contractAgreementSysNoList = [];
      setUpstreamSupplierSysNoList(updateParams.upstreamSupplierSysNoList);
    }
    const { query } = router;
    if (!updateParams.pageNo) {
      updateParams.pageNo = 1;
    }

    /*
    当前这种方式调用router.push，页面不会更新，状态不会丢，拿到的router.query不是最新的，是页面初始化时的router.query，会造成搜索条件状态始终是初始值
    */
    searchParams.current = { ...searchParams.current, ...updateParams };

    const newQuery = {
      ...query,
      params: jsurl.stringify(searchParams.current),
    };
    const url = {
      pathname: router.pathname,
      query: newQuery,
    };

    const queryStr = Object.entries(newQuery)
      .map((item) => item.join('='))
      .join('&');

    const urlStr = url.pathname + '?' + queryStr;

    router.push(url, urlStr, {
      shallow: true,
    });
  }

  // 过滤条件修改后触发
  function handleListFilterUpdateSearch(updateParams) {
    if (!updateParams) {
      // eslint-disable-next-line no-param-reassign
      updateParams = {};
    }
    updateParams.pageNo = 1;
    updateSearch(updateParams);
  }
  const { shopSysNo } = router.query;

  return (
    <>
      <Container>
        <ListSearchResult
          primaryColor={primaryColor}
          shopCustomCategoryList={shopCustomCategoryList}
          keywords={searchParams.current.keywords as string}
          total={listData.total}
        />
        <ListFilter
          primaryColor={primaryColor}
          filterList={filterList}
          updateSearch={
            isFeatureActive(FeatureEnum.FixShopSkuList)
              ? handleListFilterUpdateSearch
              : updateSearch
          }
        />
        <ListRuleBar primaryColor={primaryColor} listData={listData} updateSearch={updateSearch}>
          {isSearchPage && userInfo?.organizationSource === UserSource.inside && (
            <>
              <AgreementSelector
                updateSearch={updateSearch}
                supplierSysNoList={upstreamSupplierSysNoList}
                areaLevelCode={areaLevelCode}
                shopSysNo={shopSysNo}
              />
              <ContractSelector
                updateSearch={updateSearch}
                areaLevelCode={areaLevelCode}
                supplierSysNoList={upstreamSupplierSysNoList}
              />
            </>
          )}
        </ListRuleBar>
        {loading ? (
          <div className="loading-wrap">
            <Spin />
          </div>
        ) : (
          <SearchListContent listData={listData} updateSearch={updateSearch} />
        )}
      </Container>
      <style jsx>
        {`
          .loading-wrap {
            text-align: center;
            padding: 200px;
          }
        `}
      </style>
    </>
  );
};

export default ShopProListPC;
